import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FileEdit, Send, CheckCircle, Database, Globe, Archive,
  Loader2, AlertCircle, CheckCheck, Clock, FileSearch, Cpu
} from 'lucide-react';

// Workflow steps definition
const WORKFLOW_STEPS = [
  { id: 'draft', label: 'Draft', icon: FileEdit },
  { id: 'review', label: 'Review', icon: Send },
  { id: 'approved', label: 'Approved', icon: CheckCircle },
  { id: 'ingesting', label: 'Ingesting', icon: Database },
  { id: 'live', label: 'Live', icon: Globe },
];

// Ingestion sub-steps for showing progress. This is the sequence the pipeline
// actually writes: queued -> parsing -> analyzing -> indexed. It previously
// listed Chunking and Embedding, which nothing has written since ingestion
// moved to page indexing, so two of the five steps could never light up.
// Rows still carrying a legacy 'chunking'/'embedding' status resolve to sub-step
// 0 via getIngestionSubIndex's fallback; the membership tests below continue to
// treat them as in-progress so a stale row is not mistaken for a draft.
const INGESTION_SUB_STEPS = [
  { id: 'queued', label: 'Queued', icon: Clock },
  { id: 'parsing', label: 'Parsing', icon: FileSearch },
  { id: 'analyzing', label: 'Analyzing', icon: Cpu },
  { id: 'indexed', label: 'Indexed', icon: Database },
];

// Statuses that mean "ingestion is under way". The first three are live; the
// last two only ever appear on rows written before page indexing landed.
const IN_PROGRESS_INGESTION_STATUSES = [
  'queued', 'parsing', 'analyzing', 'chunking', 'embedding',
];

// Get current step index based on document state
function getStepIndex(doc) {
  if (!doc) return 0;
  
  const { status, ingestion_status } = doc;
  
  if (status === 'draft') return 0;
  if (status === 'pending_review') return 1;
  if (status === 'approved') return 2;
  if (status === 'live') return 4;
  if (status === 'published') {
    if (IN_PROGRESS_INGESTION_STATUSES.includes(ingestion_status)) return 3;
    // 'complete' counts like 'indexed' under honest statuses (EIP-514/497):
    // publish no longer pre-writes 'queued', so a published doc routinely
    // carries the pipeline's 'complete' until the publish task promotes it.
    if (ingestion_status === 'indexed' || ingestion_status === 'complete') return 4; // live
    if (ingestion_status === 'failed') return 3; // stuck at ingesting
    return 3; // published but ingestion state unknown - it is past approval
  }
  return 0;
}

// Get ingestion sub-step index
function getIngestionSubIndex(ingestion_status) {
  const subStepIds = INGESTION_SUB_STEPS.map(s => s.id);
  const idx = subStepIds.indexOf(ingestion_status);
  return idx >= 0 ? idx : 0;
}

// Get step status (completed, current, upcoming, error)
function getStepStatus(stepIdx, currentIdx, doc) {
  if (stepIdx < currentIdx) return 'completed';
  if (stepIdx === currentIdx) {
    // Check for error states
    if (doc?.ingestion_status === 'failed' && stepIdx === 3) return 'error';
    if ((doc?.status === 'live' || ['indexed', 'complete'].includes(doc?.ingestion_status)) && stepIdx === 4) return 'completed';
    return 'current';
  }
  return 'upcoming';
}

// Ingestion Progress Sub-Stepper
function IngestionProgressStepper({ ingestionStatus }) {
  const currentSubIdx = getIngestionSubIndex(ingestionStatus);
  
  return (
    <div className="mt-3 p-3 bg-primary/10  rounded-lg">
      <p className="text-xs text-primary  font-medium mb-3">Ingestion Progress</p>
      <div className="flex items-center justify-between">
        {INGESTION_SUB_STEPS.map((step, idx) => {
          const isCompleted = idx < currentSubIdx;
          const isCurrent = idx === currentSubIdx;
          const Icon = step.icon;
          const isLast = idx === INGESTION_SUB_STEPS.length - 1;
          
          return (
            <div key={step.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div 
                  className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors ${
                    isCompleted 
                      ? 'bg-success/100 border-success text-white' 
                      : isCurrent
                        ? 'bg-primary/100 border-primary text-white'
                        : 'bg-card border-border dark:border-border text-muted-foreground'
                  }`}
                >
                  {isCompleted ? (
                    <CheckCheck className="w-3 h-3" />
                  ) : isCurrent ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Icon className="w-3 h-3" />
                  )}
                </div>
                <span className={`text-[10px] mt-1 font-medium ${
                  isCompleted 
                    ? 'text-success ' 
                    : isCurrent
                      ? 'text-primary '
                      : 'text-muted-foreground'
                }`}>
                  {step.label}
                </span>
              </div>
              
              {!isLast && (
                <div className={`flex-1 h-0.5 mx-1 ${
                  idx < currentSubIdx 
                    ? 'bg-success/100' 
                    : 'bg-muted dark:bg-secondary'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DocumentWorkflowStepper({ document, onAction, showActions = true }) {
  if (!document) return null;

  if (document.status === 'archived') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 bg-secondary dark:bg-card/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Archive className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground dark:text-muted-foreground">
              Archived - Removed from the active workflow and search availability
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Status:</span>
          <Badge className="bg-secondary text-muted-foreground">
            Archived
          </Badge>
        </div>
      </div>
    );
  }
  
  const currentIdx = getStepIndex(document);
  const isIngesting = document.status === 'published' && IN_PROGRESS_INGESTION_STATUSES.includes(document.ingestion_status);
  const isIndexed = ['indexed', 'complete'].includes(document.ingestion_status);
  const isLive = document.status === 'live' || (document.status === 'published' && isIndexed);
  const ingestionFailed = document.ingestion_status === 'failed';

  return (
    <div className="space-y-4">
      {/* Main Workflow Stepper */}
      <div className="flex items-center justify-between w-full">
        {WORKFLOW_STEPS.map((step, idx) => {
          const status = getStepStatus(idx, currentIdx, document);
          const Icon = step.icon;
          const isLast = idx === WORKFLOW_STEPS.length - 1;
          
          return (
            <div key={step.id} className="flex items-center flex-1">
              {/* Step circle */}
              <div className="flex flex-col items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                    status === 'completed' 
                      ? 'bg-success/100 border-success text-white' 
                      : status === 'current'
                        ? 'bg-primary border-primary text-white'
                        : status === 'error'
                          ? 'bg-destructive/100 border-destructive text-white'
                          : 'bg-card border-border dark:border-border text-muted-foreground'
                  }`}
                >
                  {status === 'completed' ? (
                    <CheckCheck className="w-4 h-4" />
                  ) : status === 'error' ? (
                    <AlertCircle className="w-4 h-4" />
                  ) : status === 'current' && isIngesting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>
                <span className={`text-xs mt-1 font-medium ${
                  status === 'completed' 
                    ? 'text-success ' 
                    : status === 'current'
                      ? 'text-primary '
                      : status === 'error'
                        ? 'text-destructive '
                        : 'text-muted-foreground'
                }`}>
                  {step.label}
                </span>
              </div>
              
              {/* Connector line */}
              {!isLast && (
                <div className={`flex-1 h-0.5 mx-2 ${
                  idx < currentIdx 
                    ? 'bg-success/100' 
                    : 'bg-muted dark:bg-secondary'
                }`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Ingestion Sub-Stepper */}
      <div
        className={`grid transition-all duration-300 ease-out ${
          isIngesting && document.ingestion_status
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        }`}
        aria-hidden={!isIngesting}
      >
        <div className="min-h-0 overflow-hidden">
          <IngestionProgressStepper ingestionStatus={document.ingestion_status || 'queued'} />
        </div>
      </div>

      {/* Status message and action */}
      <div className="flex min-h-11 items-center justify-between p-3 bg-secondary dark:bg-card/50 rounded-lg transition-colors duration-200">
        <div className="flex items-center gap-2">
          {isIngesting && (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground dark:text-muted-foreground">
                Eva is learning this document ({document.ingestion_status})...
              </span>
            </>
          )}
          {ingestionFailed && (
            <>
              <AlertCircle className="w-4 h-4 text-destructive" />
              <span className="text-sm text-destructive ">
                Ingestion failed: {document.ingestion_error || 'Unknown error'}
              </span>
            </>
          )}
          {isLive && (
            <>
              <Globe className="w-4 h-4 text-success" />
              <span className="text-sm text-success ">
                Live in Eva People
              </span>
            </>
          )}
          {document.status === 'draft' && (
            <>
              <FileEdit className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground dark:text-muted-foreground">
                Add metadata and submit for review
              </span>
            </>
          )}
          {document.status === 'pending_review' && (
            <>
              <Send className="w-4 h-4 text-warning" />
              <span className="text-sm text-muted-foreground dark:text-muted-foreground">
                Waiting for approver review
              </span>
            </>
          )}
          {document.status === 'approved' && (
            <>
              <CheckCircle className="w-4 h-4 text-info" />
              <span className="text-sm text-muted-foreground dark:text-muted-foreground">
                Ready to publish
              </span>
            </>
          )}
        </div>

        {/* Action buttons - conditionally shown */}
        {showActions && (
          <div className="flex gap-2">
            {ingestionFailed && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onAction?.('retry-ingestion')}
              >
                Retry Ingestion
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
